import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatusBadge = ({ status }) => {
  const colorMap = {
    queued: "text-blue-400 border-blue-400",
    processing: "text-yellow-400 border-yellow-400",
    drafted: "text-purple-400 border-purple-400",
    published: "text-emerald-400 border-emerald-400",
    qa_failed: "text-red-400 border-red-400",
    error: "text-red-400 border-red-400",
  };
  
  return (
    <span className={`inline-flex px-2 py-0.5 text-xs font-mono uppercase tracking-wide border rounded-full ${colorMap[status] || 'text-zinc-400 border-zinc-400'}`}>
      {status}
    </span>
  );
};

export default function Queue() {
  const [queue, setQueue] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newKeyword, setNewKeyword] = useState({
    keyword: "",
    page_type: "blog",
    target_domain: "jigsawconferences.co.uk",
    priority: 0,
  });

  const fetchData = useCallback(async () => {
    try {
      const [queueRes, statsRes] = await Promise.all([
        axios.get(`${API}/queue`),
        axios.get(`${API}/queue/stats`)
      ]);
      setQueue(queueRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Error fetching queue:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleAdd = async () => {
    try {
      await axios.post(`${API}/queue`, newKeyword);
      toast.success("Keyword added to queue");
      setDialogOpen(false);
      setNewKeyword({
        keyword: "",
        page_type: "blog",
        target_domain: "jigsawconferences.co.uk",
        priority: 0,
      });
      fetchData();
    } catch (error) {
      toast.error("Failed to add keyword");
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/queue/${id}`);
      toast.success("Keyword removed");
      fetchData();
    } catch (error) {
      toast.error("Failed to remove keyword");
    }
  };

  const handleTypeChange = (value) => {
    const domainMap = {
      blog: "jigsawconferences.co.uk",
      directory: "venues.jigsawconferences.co.uk",
      event: "events.jigsawconferences.co.uk"
    };
    setNewKeyword(prev => ({
      ...prev,
      page_type: value,
      target_domain: domainMap[value]
    }));
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-full">
        <RefreshCw className="h-6 w-6 animate-spin text-zinc-500" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="queue-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-mono font-medium text-white">Queue</h1>
          <p className="text-sm text-zinc-500">Manage keywords and topics for generation</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button size="sm" className="bg-white text-black hover:bg-zinc-200" data-testid="add-keyword-btn">
              <Plus className="h-4 w-4 mr-1" />
              Add Keyword
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-900 border-zinc-800">
            <DialogHeader>
              <DialogTitle className="text-white">Add Keyword to Queue</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div className="space-y-2">
                <Label className="text-zinc-400">Keyword / Topic</Label>
                <Input
                  value={newKeyword.keyword}
                  onChange={(e) => setNewKeyword(prev => ({ ...prev, keyword: e.target.value }))}
                  placeholder="e.g., Meeting Rooms in London"
                  className="bg-zinc-800 border-zinc-700 text-white"
                  data-testid="keyword-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400">Page Type</Label>
                <Select value={newKeyword.page_type} onValueChange={handleTypeChange}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white" data-testid="type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    <SelectItem value="blog">Blog Article</SelectItem>
                    <SelectItem value="directory">Directory Page</SelectItem>
                    <SelectItem value="event">Event Guide</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400">Target Domain</Label>
                <Input
                  value={newKeyword.target_domain}
                  disabled
                  className="bg-zinc-800 border-zinc-700 text-zinc-500"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400">Priority (0-10)</Label>
                <Input
                  type="number"
                  min="0"
                  max="10"
                  value={newKeyword.priority}
                  onChange={(e) => setNewKeyword(prev => ({ ...prev, priority: parseInt(e.target.value) || 0 }))}
                  className="bg-zinc-800 border-zinc-700 text-white"
                  data-testid="priority-input"
                />
              </div>
              <Button onClick={handleAdd} className="w-full bg-white text-black hover:bg-zinc-200" data-testid="submit-keyword-btn">
                Add to Queue
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="pt-4">
            <div className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Total</div>
            <div className="font-mono text-2xl text-white">{stats?.total || 0}</div>
          </CardContent>
        </Card>
        {Object.entries(stats?.by_status || {}).slice(0, 3).map(([status, count]) => (
          <Card key={status} className="bg-zinc-900 border-zinc-800">
            <CardContent className="pt-4">
              <div className="text-xs text-zinc-500 uppercase tracking-wider mb-1">{status}</div>
              <div className="font-mono text-2xl text-white">{count}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Queue Table */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-sm font-mono text-zinc-300">
            Queue Items ({queue.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-800">
                  <TableHead className="text-zinc-500 text-xs uppercase">Keyword</TableHead>
                  <TableHead className="text-zinc-500 text-xs uppercase">Type</TableHead>
                  <TableHead className="text-zinc-500 text-xs uppercase">Domain</TableHead>
                  <TableHead className="text-zinc-500 text-xs uppercase">Status</TableHead>
                  <TableHead className="text-zinc-500 text-xs uppercase">Priority</TableHead>
                  <TableHead className="text-zinc-500 text-xs uppercase w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {queue.map((item) => (
                  <TableRow key={item.id} className="border-zinc-800" data-testid={`queue-item-${item.id}`}>
                    <TableCell className="text-white font-medium">{item.keyword}</TableCell>
                    <TableCell className="font-mono text-xs text-zinc-400">{item.page_type}</TableCell>
                    <TableCell className="font-mono text-xs text-zinc-500">{item.target_domain}</TableCell>
                    <TableCell><StatusBadge status={item.status} /></TableCell>
                    <TableCell className="font-mono text-zinc-400">{item.priority}</TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(item.id)}
                        className="text-zinc-500 hover:text-red-400"
                        data-testid={`delete-${item.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {queue.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-zinc-500">
                      No items in queue. Add keywords or seed demo content.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
